import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ImagePlus, Loader2, Repeat, Trash2 } from "lucide-react";
import { CRM_API, errMsg } from "./api";
import { toast } from "sonner";

const photoUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/crm/site-media/${id}/file`;

export default function SiteMedia() {
  const [sections, setSections] = useState([]);
  const [section, setSection] = useState("");
  const [role, setRole] = useState("before");
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    CRM_API.get("/site-media/sections")
      .then(({ data }) => {
        setSections(data);
        if (data.length) setSection((s) => s || data[0].key);
      })
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const load = useCallback(async (sec) => {
    if (!sec) return;
    setLoading(true);
    try {
      const { data } = await CRM_API.get("/site-media", { params: { section: sec } });
      setMedia(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(section); }, [section, load]);

  const upload = async (files) => {
    if (!files?.length || !section) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("section", section);
        fd.append("role", role);
        fd.append("file", f);
        await CRM_API.post("/site-media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Uploaded to the website section");
      await load(section);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    try {
      await CRM_API.delete(`/site-media/${id}`);
      setMedia((prev) => prev.filter((m) => m.id !== id));
      toast.success("Removed from the website");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleRole = async (m) => {
    setBusyId(m.id);
    try {
      await CRM_API.patch(`/site-media/${m.id}`, { role: m.role === "before" ? "after" : "before" });
      await load(section);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusyId(null); }
  };

  // Reorder inside one role column: swap with neighbour, then persist the whole column order
  const move = async (m, dir, items) => {
    const idx = items.findIndex((x) => x.id === m.id);
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    setBusyId(m.id);
    try {
      await CRM_API.post("/site-media/reorder", {
        section, role: m.role, ids: next.map((x) => x.id),
      });
      await load(section);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusyId(null); }
  };

  const befores = useMemo(() => media.filter((m) => m.role === "before"), [media]);
  const afters = useMemo(() => media.filter((m) => m.role === "after"), [media]);

  const Column = ({ title, items, accent }) => (
    <div className="flex-1 min-w-0">
      <div className={`font-mono-alt text-[10px] uppercase tracking-widest mb-2 ${accent}`}>{title} ({items.length})</div>
      <div className="grid grid-cols-2 gap-2">
        {items.map((m, i) => (
          <div key={m.id} className={`relative aspect-[4/3] rounded-lg overflow-hidden bg-white/5 group ${busyId === m.id ? "opacity-50" : ""}`} data-testid={`sm-photo-${m.id}`}>
            <img src={photoUrl(m.id)} alt="" className="w-full h-full object-cover" loading="lazy" />
            <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 font-mono-alt text-[9px] text-white/70">
              #{i + 1}
            </span>
            <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => move(m, -1, items)} disabled={i === 0} title="Move up"
                className="p-1.5 bg-black/70 text-white/80 rounded-md hover:bg-black/90 disabled:opacity-30"
                data-testid={`sm-up-${m.id}`}>
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => move(m, 1, items)} disabled={i === items.length - 1} title="Move down"
                className="p-1.5 bg-black/70 text-white/80 rounded-md hover:bg-black/90 disabled:opacity-30"
                data-testid={`sm-down-${m.id}`}>
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => toggleRole(m)} title={m.role === "before" ? "Make it After" : "Make it Before"}
                className="p-1.5 bg-[#1F5EFF]/80 text-white rounded-md hover:bg-[#1F5EFF]"
                data-testid={`sm-role-toggle-${m.id}`}>
                <Repeat className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => remove(m.id)} title="Remove from website"
                className="p-1.5 bg-red-500/80 text-white rounded-md hover:bg-red-500"
                data-testid={`sm-delete-${m.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-2 border border-dashed border-white/10 rounded-lg py-8 text-center text-white/25 text-xs">
            No photos yet
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="site-media-page">
      <h1 className="text-2xl font-bold text-white mb-1">Website Photos</h1>
      <p className="text-white/45 text-sm mb-6">
        Upload real work photos to any service page. Before/after pairs show side by side — keep them in matching order (#1 Before pairs with #1 After). Hover a photo to reorder, switch before/after, or delete.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 mb-6">
        <select
          value={section}
          onChange={(e) => setSection(e.target.value)}
          className="bg-[#101318] border border-white/12 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-[#1F5EFF]"
          data-testid="sm-section-select"
        >
          {sections.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
        <div className="flex rounded-lg overflow-hidden border border-white/12">
          {[["before", "Before"], ["after", "After"]].map(([r, label]) => (
            <button
              key={r}
              onClick={() => setRole(r)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors ${role === r ? "bg-[#1F5EFF] text-white" : "bg-white/5 text-white/50 hover:text-white"}`}
              data-testid={`sm-role-${r}`}
            >
              {label}
            </button>
          ))}
        </div>
        <label className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#1F5EFF]/15 border border-[#1F5EFF]/40 text-[#8db2ff] text-sm font-semibold cursor-pointer hover:bg-[#1F5EFF]/25 transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
          data-testid="sm-upload">
          <input
            type="file" accept="image/*" multiple className="hidden"
            onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; upload(f); }}
          />
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
          Upload {role}
        </label>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/40 text-sm py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-6" data-testid="sm-columns">
          <Column title="Before" items={befores} accent="text-white/50" />
          <Column title="After" items={afters} accent="text-emerald-300" />
        </div>
      )}
    </div>
  );
}
